import {useCallback, useState} from 'react';
import {FilterSectionLayout} from 'components/FilterSectionLayout';
import {useSearch} from 'features/Translations/hooks';
import {Shortcut, ShortcutProvider} from '@shopify/react-shortcuts';
import {SearchIcon} from '@shopify/polaris-icons';
import {PlainAction} from '@shopify/online-store-ui';
import {Box, Button, InlineStack} from '@shopify/polaris';
import {useI18n} from '@shopify/react-i18n';

export interface Props {
  disabled?: boolean;
  mobile?: boolean;
}

const scope = 'SearchInput';

export const SearchInput = ({disabled = false, mobile = false}: Props) => {
  const [i18n] = useI18n();
  const [isFocused, setIsFocused] = useState(false);
  const {
    isActive,
    query,
    setQuery,
    setLocaleFilter,
    setResourceTypeFilter,
    toggleSearch,
  } = useSearch();

  const clearQuery = useCallback(() => {
    setQuery('');
    setLocaleFilter(null);
    setResourceTypeFilter(null);
  }, [setLocaleFilter, setQuery, setResourceTypeFilter]);

  const clearAndClose = useCallback(() => {
    clearQuery();
    toggleSearch(false);
    setIsFocused(false);
  }, [clearQuery, toggleSearch]);

  const searchInputMarkup = (
    <FilterSectionLayout
      filterOptions={{
        filters: [],
        appliedFilters: [],
        hideTags: true,
        queryValue: query,
        onQueryChange: setQuery,
        onQueryClear: () => clearQuery(),
        onQueryFocus: () => {
          if (mobile) toggleSearch(true);
          setIsFocused(true);
        },
        onQueryBlur: () => {
          if (mobile) toggleSearch(false);
          setIsFocused(false);
        },
        queryPlaceholder: i18n.translate('placeholder', {scope}),
        disabled,
        alwaysVisibleClearButton: true,
        focused: isFocused,
      }}
    />
  );

  const mobileMarkup = (
    <Box
      paddingBlockEnd="300"
      paddingBlockStart="300"
      paddingInlineEnd="400"
      paddingInlineStart="400"
    >
      {searchInputMarkup}
    </Box>
  );

  const desktopMarkup = (
    <InlineStack gap="400" align="center">
      <div style={{flexGrow: 1}}>{searchInputMarkup}</div>
      <Button
        variant="monochromePlain"
        removeUnderline
        onClick={() => clearAndClose()}
      >
        {i18n.translate('Common.cancel')}
      </Button>
    </InlineStack>
  );

  const activator = (
    <PlainAction
      onAction={() => {
        toggleSearch(true);
        setIsFocused(true);
      }}
      disabled={disabled}
      truncate
      icon={SearchIcon}
      accessibilityLabel={i18n.translate('placeholder', {scope})}
    />
  );

  let markup;
  if (mobile) {
    markup = mobileMarkup;
  } else {
    markup = isActive ? desktopMarkup : activator;
  }

  return (
    <ShortcutProvider>
      {markup}
      <Shortcut
        ordered={['/']}
        onMatch={() => {
          if (
            document.activeElement?.className.includes(
              'Polaris-Popover__Content',
            )
          )
            return;
          toggleSearch(true);
          setIsFocused(true);
        }}
      />
    </ShortcutProvider>
  );
};
